<template>
  <div class="auth-page">
    <div class="auth-brand">
      <img
        src="/icons/icon.svg"
        alt=""
        class="auth-mark"
        width="56"
        height="56"
      >
      <p class="kicker">{{ t('nav.brand') }}</p>
    </div>
    <h1>{{ t('auth.registerTitle') }}</h1>
    <p class="lead">{{ t('auth.registerLead') }}</p>

    <form class="panel" @submit.prevent="submit">
      <label class="field">
        <span>{{ t('auth.email') }}</span>
        <input
          v-model="email"
          type="email"
          autocomplete="email"
          required
        />
      </label>
      <label class="field">
        <span>{{ t('auth.username') }}</span>
        <input
          v-model="username"
          type="text"
          autocomplete="nickname"
          :maxlength="usernameMaxLen"
          :placeholder="t('profile.placeholder')"
          required
        />
      </label>
      <label class="field">
        <span>{{ t('auth.password') }}</span>
        <PasswordInput
          v-model="password"
          autocomplete="new-password"
          :minlength="passwordMinLen"
          :placeholder="t('auth.passwordHint')"
          required
        />
      </label>
      <p v-if="errorKey" class="error">{{ t(errorKey) }}</p>
      <button type="submit" class="cta" :disabled="auth.busy">
        {{ t('auth.createAccount') }}
      </button>
    </form>

    <p class="switch">
      {{ t('auth.haveAccount') }}
      <router-link :to="loginTo">{{ t('auth.goLogin') }}</router-link>
    </p>

    <div class="discord-row">
      <DiscordJoinButton />
    </div>

    <div class="auth-lang">
      <LanguageSelect />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import LanguageSelect from '../components/LanguageSelect.vue'
import { useI18n } from '../i18n/t'
import { ApiError } from '../api/http'
import { PASSWORD_MIN_LEN, useAuthStore } from '../stores/auth'
import { useLocaleStore } from '../stores/locale'
import { USERNAME_MAX_LEN } from '../stores/player'
import DiscordJoinButton from '../components/DiscordJoinButton.vue'
import PasswordInput from '../components/PasswordInput.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const email = ref('')
const username = ref('')
const password = ref('')
const errorKey = ref('')
const usernameMaxLen = USERNAME_MAX_LEN
const passwordMinLen = PASSWORD_MIN_LEN

const loginTo = computed(() => ({
  name: 'login',
  query: route.query.redirect ? { redirect: route.query.redirect } : {},
}))

function errorToKey(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'email_domain') return 'auth.errors.email_domain'
    if (err.code === 'email_taken') return 'auth.errors.email_taken'
    if (err.code === 'username_taken') return 'auth.errors.username_taken'
    if (err.code === 'rate_limited') return 'auth.errors.rate_limited'
    if (err.code === 'database_unavailable') return 'auth.errors.database_unavailable'
    if (err.code === 'validation') return 'auth.errors.validation'
    if (err.status === 503) return 'auth.errors.database_unavailable'
  }
  return 'auth.errors.network'
}

function safeRedirect(): string {
  const raw = route.query.redirect
  if (typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//')) {
    return raw
  }
  return '/'
}

async function submit(): Promise<void> {
  errorKey.value = ''
  try {
    await auth.register({
      email: email.value,
      username: username.value,
      password: password.value,
      locale: useLocaleStore().locale,
    })
    await router.replace(safeRedirect())
  } catch (err) {
    errorKey.value = errorToKey(err)
  }
}
</script>

<style scoped>
.auth-page {
  width: min(440px, 100%);
  margin: 0 auto;
  padding: 48px 16px 64px;
  font-family: Roboto, system-ui, sans-serif;
  color: var(--md-sys-color-on-surface);
}

.auth-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.auth-mark {
  width: 56px;
  height: 56px;
  border-radius: 14px;
}

.kicker {
  margin: 0 0 8px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h1 {
  margin: 0 0 8px;
  font-size: 36px;
  line-height: 44px;
  font-weight: 400;
}

.lead {
  margin: 0 0 24px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--md-sys-color-on-surface-variant);
}

.field input {
  min-height: 44px;
  padding: 10px 12px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 16px;
  font-weight: 400;
}

.field input:focus {
  outline: none;
  border-bottom-width: 2px;
  border-bottom-color: var(--md-sys-color-primary);
}

.cta {
  min-height: 44px;
  border: 0;
  border-radius: 8px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.cta:disabled {
  opacity: 0.6;
  cursor: wait;
}

.error {
  margin: 0;
  color: #e07070;
  font-size: 14px;
  line-height: 20px;
}

.switch {
  margin: 20px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  text-align: center;
}

.switch a {
  color: var(--md-sys-color-primary);
  font-weight: 600;
  text-decoration: none;
}

.discord-row {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.auth-lang {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}
</style>
